// Integração com o app iOS/Android (Capacitor). Na web nada daqui é usado.
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { StatusBar, Style } from "@capacitor/status-bar";

export const isNativeApp = Capacitor.isNativePlatform();

// O Hosted UI do Cognito volta para o app por este custom scheme (CallbackURLs/LogoutURLs
// do app client em template-auth.yaml)
const APP_SCHEME = "br.com.buildcheap.app://";
export const NATIVE_REDIRECT_URI = `${APP_SCHEME}callback`;
export const NATIVE_LOGOUT_URI = `${APP_SCHEME}logout`;

// Origem da versão web; também usada para gerar links públicos (no app window.location.origin é https://localhost)
export const WEB_APP_ORIGIN = "https://app.buildcheap.com.br";
const UNIVERSAL_LINK_ORIGIN = WEB_APP_ORIGIN;

// Resolve a navegação pendente do oidc-client-ts (login/logout concluído ou navegador fechado)
let finishNavigation = null;
const finishPendingNavigation = () => {
  finishNavigation?.();
  finishNavigation = null;
};

/**
 * Navigator do oidc-client-ts para o app: abre o Hosted UI no navegador in-app
 * (SFSafariViewController / Custom Tabs), já que o Google bloqueia login em WebView.
 * A promise só termina quando o callback chega (handleAppUrl) ou o usuário fecha o
 * navegador, liberando o activeNavigator/isLoading do react-oidc-context.
 */
export const inAppBrowserNavigator = {
  prepare: async () => ({
    navigate: async ({ url }) => {
      finishPendingNavigation();
      const finished = new Promise((resolve) => (finishNavigation = resolve));
      await Browser.open({ url, presentationStyle: "fullscreen" });
      await finished;
      return { url: "" };
    },
    close: finishPendingNavigation,
  }),
  callback: async () => {},
};

if (isNativeApp) {
  Browser.addListener("browserFinished", finishPendingNavigation);
  // Texto escuro na status bar: o header do app é branco (inclusive no modo escuro do sistema)
  StatusBar.setStyle({ style: Style.Light }).catch(() => {});
}

export const openInAppBrowser = (url) => Browser.open({ url, presentationStyle: "fullscreen" });

/**
 * Trata URLs que abrem o app: retorno do login/logout (custom scheme) e Universal/App
 * Links de app.buildcheap.com.br (ex.: links de cotação enviados pelo WhatsApp).
 */
// Cada callback de login (code de uso único) é processado uma vez só
const handledCallbacks = new Set();

export const handleAppUrl = async (url, { onSigninCallback, onSignoutCallback, navigate }) => {
  if (url.startsWith(NATIVE_REDIRECT_URI)) {
    if (handledCallbacks.has(url)) return;
    handledCallbacks.add(url);
    await Browser.close().catch(() => {});
    finishPendingNavigation();
    await onSigninCallback(url);
  } else if (url.startsWith(NATIVE_LOGOUT_URI)) {
    await Browser.close().catch(() => {});
    onSignoutCallback?.();
  } else if (url.startsWith(UNIVERSAL_LINK_ORIGIN)) {
    const { pathname, search, hash } = new URL(url);
    navigate(`${pathname}${search}${hash}`);
  }
};

// A URL de abertura (cold start) só é lida uma vez: o getLaunchUrl do iOS devolve a
// última URL recebida, inclusive callbacks de login já tratados pelo appUrlOpen
let launchUrlChecked = false;

// Registra o listener de URLs e processa a URL que abriu o app (cold start)
export const listenAppUrls = (handlers) => {
  const listener = App.addListener("appUrlOpen", ({ url }) => handleAppUrl(url, handlers));
  if (!launchUrlChecked) {
    launchUrlChecked = true;
    App.getLaunchUrl().then((launch) => launch?.url && handleAppUrl(launch.url, handlers));
  }
  return () => listener.then((l) => l.remove());
};
